/**
 * O megafone do topo (spec 036): ao lado do sino, com o número de novidades
 * que a pessoa ainda não viu. Abre o painel "Novidades": cartões agrupados por
 * dia, cada um com título, resumo, "Ler mais", imagem e a ação ("Ver
 * novidade"), que leva à tela e começa o tour. Abrir o painel marca o que ele
 * mostra como visto (na API, D2); o ponto azul dos cartões novos fica até o
 * painel fechar.
 */
import { useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Megaphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { EmptyState } from '@/components/ui/misc';
import { cn } from '@/lib/utils';
import { NewsBody, NewsImages, NewsText } from './news-content';
import { useNews } from './news-queries';
import { startNewsTour } from './news-tour';
import { groupByDay, newsAnchorId, type NewsAudience, type NewsEntry } from './news-model';

export const newsHistoryPath = (audience: NewsAudience) => (audience === 'portal' ? '/portal/novidades' : '/novidades');

/** navega para a tela da novidade e, se ela tem tour, o começa lá */
export function useRunNewsAction() {
  const navigate = useNavigate();
  return (entry: NewsEntry) => {
    const action = entry.action;
    if (!action) return;
    void navigate({ to: action.to as never, search: (action.search ?? {}) as never });
    if (action.startsTour && entry.tour) startNewsTour(entry.tour);
  };
}

export function NewsMegaphone({ audience, tone = 'default' }: { audience: NewsAudience; tone?: 'default' | 'dark' }) {
  const news = useNews(audience);
  const runAction = useRunNewsAction();
  const [open, setOpen] = useState(false);
  /** as que eram novas quando o painel abriu: o ponto fica até fechar */
  const [fresh, setFresh] = useState<ReadonlySet<string>>(new Set());
  const [expanded, setExpanded] = useState<string | null>(null);

  // o portal só ganha megafone quando houver novidade para o portal
  if (audience === 'portal' && news.visible.length === 0) return null;

  const count = news.unseen.length;
  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      setFresh(new Set(news.unseen.map((e) => e.id)));
      news.markSeen(news.visible.map((e) => e.id));
    } else {
      setExpanded(null);
    }
  };

  const act = (entry: NewsEntry) => {
    setOpen(false);
    runAction(entry);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-tour="novidades-megafone"
          aria-label={count > 0 ? `Novidades (${count} não vistas)` : 'Novidades'}
          title="Novidades"
          className={cn(
            'relative flex size-8 items-center justify-center rounded-full outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring/50',
            tone === 'dark'
              ? 'text-white/75 hover:bg-white/10 hover:text-white'
              : 'text-muted-foreground hover:bg-accent hover:text-foreground'
          )}
        >
          <Megaphone className="size-[18px]" />
          {count > 0 ? (
            <span
              aria-hidden
              className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] leading-none font-semibold text-primary-foreground"
            >
              {count > 9 ? '9+' : count}
            </span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[400px] max-w-[calc(100vw-1rem)] p-0">
        <div className="flex items-center justify-between border-b border-divider px-4 py-3">
          <span className="text-sm font-semibold">Novidades</span>
          <Link
            to={newsHistoryPath(audience) as never}
            onClick={() => setOpen(false)}
            className="text-[13px] font-medium text-primary hover:underline"
          >
            Ver todas
          </Link>
        </div>
        {news.visible.length === 0 ? (
          <EmptyState message="Nenhuma novidade por aqui ainda." className="h-32" />
        ) : (
          <div className="max-h-[min(70vh,560px)] overflow-y-auto px-4 pb-4">
            {groupByDay(news.visible).map((group) => (
              <section key={group.key} aria-label={group.heading}>
                <h3 className="sticky top-0 z-10 bg-popover pt-3 pb-2 text-xs font-semibold text-muted-foreground">
                  {group.heading}
                </h3>
                <ul className="space-y-3">
                  {group.entries.map((entry) => {
                    const isOpen = expanded === entry.id;
                    return (
                      <li key={entry.id} className="rounded-lg border bg-card p-3">
                        <div className="flex items-start gap-2">
                          {fresh.has(entry.id) ? (
                            <span aria-label="nova" className="mt-1.5 size-2 shrink-0 rounded-full bg-primary" />
                          ) : null}
                          <h4 className="text-sm leading-snug font-semibold text-foreground">
                            <NewsText text={entry.title} vars={news.vars} />
                          </h4>
                        </div>
                        {isOpen ? (
                          <NewsBody blocks={entry.body} vars={news.vars} className="mt-2 text-[13px]" />
                        ) : (
                          <p className="mt-1 line-clamp-3 text-[13px] leading-relaxed text-muted-foreground">
                            <NewsText text={entry.summary} vars={news.vars} />
                          </p>
                        )}
                        <div className="mt-1">
                          {isOpen ? (
                            <Link
                              to={newsHistoryPath(audience) as never}
                              hash={newsAnchorId(entry.id)}
                              onClick={() => setOpen(false)}
                              className="text-[13px] font-medium text-primary hover:underline"
                            >
                              Ver no histórico
                            </Link>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setExpanded(entry.id)}
                              className="text-[13px] font-medium text-primary hover:underline"
                            >
                              Ler mais
                            </button>
                          )}
                        </div>
                        {entry.images?.length ? (
                          <div className="mt-2">
                            <NewsImages images={entry.images} vars={news.vars} />
                          </div>
                        ) : null}
                        {entry.action ? (
                          <Button size="sm" className="mt-3 w-full" onClick={() => act(entry)}>
                            {entry.action.label}
                          </Button>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
